'use client';

import {
    type FormEvent,
    type ReactElement,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useNavigate } from '@/hooks/use-navigate';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import SvgClose from '@/components/icons-eataly/SvgClose';
import SvgRight from '@/components/icons-eataly/SvgRight';
import SvgSearch from '@/components/icons-eataly/SvgSearch';
import { cn } from '@/lib/utils';
import SearchSuggestionsPanel from './suggestions-panel';
import { useMeasuredWidth } from './hooks/use-measured-width';
import { useRecentSearchSession } from './hooks/use-recent-search-session';
import { useSearchBarSuggestions } from './hooks/use-search-bar-suggestions';
import { useSearchOverlayFocusTrap } from './hooks/use-search-overlay-focus-trap';
import { useSearchComboboxA11y } from './hooks/use-search-combobox-a11y';
import { useSuggestionsSurfaceFocus } from './hooks/use-suggestions-surface-focus';
import type { SearchBarUiState } from './types';
import { SEARCH_QUERY_MAX_LENGTH } from './search-constants';
import { useBlockScrolling } from 'src/hooks/use-block-scrolling';

/** Matches desktop trigger / modal input height (`h-10`). */
const DESKTOP_SEARCH_TRIGGER_HEIGHT_PX = 40;

export default function SearchBar({
    uiState = 'auto',
    variant = 'desktop',
    onActiveChange,
}: {
    uiState?: SearchBarUiState;
    variant?: 'desktop' | 'mobile';
    onActiveChange?: (active: boolean) => void;
}): ReactElement {
    const { t } = useTranslation('header');
    const { t: tSearch } = useTranslation('search');

    const placeholder = t('searchPlaceholderEataly');
    const openSuggestionsHint = tSearch('suggestions.openSuggestionsKeyboardHint');
    const navigate = useNavigate();
    const config = useConfig();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const desktopSearchTriggerRef = useRef<HTMLButtonElement | null>(null);
    const desktopOverlayRootRef = useRef<HTMLDivElement | null>(null);
    const desktopSuggestionsPanelRef = useRef<HTMLDivElement | null>(null);
    const popoverContentRef = useRef<HTMLDivElement | null>(null);
    const mobileOverlayRootRef = useRef<HTMLDivElement | null>(null);
    const mobileSuggestionsPanelRef = useRef<HTMLDivElement | null>(null);
    const mobileInputRef = useRef<HTMLInputElement | null>(null);
    const mobileSearchTriggerRef = useRef<HTMLButtonElement | null>(null);

    const [query, setQuery] = useState('');
    const [queryTooLong, setQueryTooLong] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    /** Set to true after ESC closes the mobile overlay; cleared on the next open or query change. */
    const [suppressMobileOverlay, setSuppressMobileOverlay] = useState(false);
    const mobileOverlayOpen = variant === 'mobile' && isFocused && !suppressMobileOverlay;
    const desktopModalOpen = variant === 'desktop' && showSuggestions;
    useBlockScrolling(mobileOverlayOpen);

    const returnFocusToMobileTriggerRef = useRef(false);

    const { recentSearches, saveRecentSearch, clearRecentSearches, removeRecentSearch } = useRecentSearchSession();

    const { trendingItems, typingLeftItems, typingCategoryItems } = useSearchBarSuggestions(
        query,
        config.pages.search.suggestionsDebounce
    );

    const { searchInputHintId, mobileSearchInputHintId } = useSearchComboboxA11y();

    const { focusFirstInSuggestionsSurface } = useSuggestionsSurfaceFocus({
        variant,
        desktopSuggestionsPanelRef,
        mobileSuggestionsPanelRef,
    });

    useSearchOverlayFocusTrap({
        active: mobileOverlayOpen,
        containerRef: mobileOverlayRootRef,
    });

    useSearchOverlayFocusTrap({
        active: desktopModalOpen,
        containerRef: desktopOverlayRootRef,
    });

    const closeDesktopModal = useCallback((returnFocusToTrigger = true) => {
        setShowSuggestions(false);
        setQuery('');
        setQueryTooLong(false);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        setIsFocused(false);
        if (returnFocusToTrigger) {
            requestAnimationFrame(() => desktopSearchTriggerRef.current?.focus());
        }
    }, []);

    const clearQuery = useCallback(() => {
        setQuery('');
        setQueryTooLong(false);
        if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.focus();
        }
        if (mobileInputRef.current) {
            mobileInputRef.current.value = '';
            mobileInputRef.current.focus();
        }
    }, []);

    const clearAndCloseDesktop = useCallback(() => {
        closeDesktopModal(true);
    }, [closeDesktopModal]);

    const openMobileOverlay = useCallback(() => {
        setSuppressMobileOverlay(false);
        setIsFocused(true);
    }, []);

    const closeMobileOverlay = useCallback((returnFocusToTrigger = true) => {
        returnFocusToMobileTriggerRef.current = returnFocusToTrigger;
        if (returnFocusToTrigger) setSuppressMobileOverlay(true);
        setShowSuggestions(false);
        setIsFocused(false);
        mobileInputRef.current?.blur();
    }, []);

    /** Typing always reveals the suggestions surface on mobile. */
    useEffect(() => {
        if (variant === 'mobile' && query.trim().length > 0) {
            setShowSuggestions(true);
        }
    }, [query, variant]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSuppressMobileOverlay(false);
        setQueryTooLong(false);
        setQuery(value);
    }, []);

    const handleSubmit = useCallback(
        (e: FormEvent) => {
            e.preventDefault();
            const rawValue = inputRef.current?.value ?? mobileInputRef.current?.value ?? '';
            const searchQuery = rawValue.trim();
            if (!searchQuery) {
                return;
            }
            if (searchQuery.length > SEARCH_QUERY_MAX_LENGTH) {
                setQueryTooLong(true);
                return;
            }
            setQueryTooLong(false);
            closeDesktopModal(false);
            closeMobileOverlay(false);

            void navigate(`/search?q=${encodeURIComponent(searchQuery)}`, { state: { query: searchQuery } });
        },
        [navigate, closeDesktopModal, closeMobileOverlay]
    );

    const closeAndNavigate = useCallback(
        (link: string, options?: { persistQuery?: string }) => {
            const persistQuery = options?.persistQuery?.trim();
            if (persistQuery) {
                saveRecentSearch(persistQuery);
            }
            inputRef.current?.blur();
            mobileInputRef.current?.blur();
            setShowSuggestions(false);
            setQuery('');
            if (inputRef.current) inputRef.current.value = '';
            if (mobileInputRef.current) mobileInputRef.current.value = '';
            if (link) void navigate(link);
        },
        [navigate, saveRecentSearch]
    );

    const resolvedState: Exclude<SearchBarUiState, 'auto'> = useMemo(() => {
        if (uiState !== 'auto') return uiState;
        const trimmed = query.trim();
        if (trimmed.length > 0) return 'typing';
        if (variant === 'desktop') return showSuggestions ? 'active' : 'default';
        return isFocused ? 'active' : 'default';
    }, [uiState, isFocused, query, showSuggestions, variant]);

    const showControls = query.trim().length > 0;

    useEffect(() => {
        onActiveChange?.(resolvedState !== 'default');
    }, [onActiveChange, resolvedState]);

    const desktopSuggestionsWidth = useMeasuredWidth(desktopSearchTriggerRef, desktopModalOpen);

    const desktopInputClassName = cn(
        'w-full',
        'h-10 border border-[var(--sand-90)] bg-[var(--sand-95)] !py-2 text-base leading-6 tracking-[var(--tracking-body-l)] md:text-base',
        'focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--sand-90)]',
        'rounded-t-[var(--corner-16)] rounded-b-none shadow-none',
        'text-[color:var(--neutral-10)] caret-[color:var(--brown-30)]',
        'pl-11 pr-12',
        query.trim().length === 0
            ? 'placeholder:text-[color:var(--neutral-10)]'
            : 'placeholder:text-[color:var(--neutral-40)]'
    );

    const desktopTriggerClassName = cn(
        'relative w-full text-left',
        'h-10 border border-[var(--sand-90)] bg-[var(--sand-95)] py-2 text-base leading-6 tracking-[var(--tracking-body-l)] md:text-base',
        'rounded-[var(--corner-24)] shadow-[0px_4px_12px_0px_rgba(0,0,0,0.05)]',
        'text-[color:var(--neutral-10)]',
        'pl-11 pr-12',
        'hover:bg-[color:color-mix(in_srgb,var(--sand-95),white_30%)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
    );

    const popoverContentClassName = cn(
        'p-0 border-0 shadow-none bg-transparent rounded-none outline-none ring-0',
        'max-w-none',
        'max-h-[min(70vh,32rem)] overflow-y-auto overflow-x-hidden',
        'data-[side=bottom]:slide-in-from-top-0'
    );

    useEffect(() => {
        if (!mobileOverlayOpen) return;
        requestAnimationFrame(() => mobileInputRef.current?.focus());
    }, [mobileOverlayOpen]);

    useEffect(() => {
        if (!desktopModalOpen) return;
        requestAnimationFrame(() => inputRef.current?.focus());
    }, [desktopModalOpen]);

    useLayoutEffect(() => {
        if (mobileOverlayOpen) return;
        if (!returnFocusToMobileTriggerRef.current) return;
        returnFocusToMobileTriggerRef.current = false;
        mobileSearchTriggerRef.current?.focus();
    }, [mobileOverlayOpen]);

    const mobileOverlay =
        mobileOverlayOpen && typeof document !== 'undefined'
            ? createPortal(
                  <div
                      ref={mobileOverlayRootRef}
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby={mobileSearchInputHintId}
                      className="fixed inset-0 z-[60] bg-neutral-white text-foreground"
                      onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                              e.preventDefault();
                              e.stopPropagation();
                              closeMobileOverlay();
                          }
                      }}>
                      <div className="px-4 pt-4">
                          <div className="flex items-center gap-3">
                              <div className="flex-1">
                                  <form onSubmit={handleSubmit} className="relative z-10">
                                      <span id={mobileSearchInputHintId} className="sr-only">
                                          {openSuggestionsHint}
                                      </span>
                                      <div className="relative">
                                          <Input
                                              id="mobile-search-input"
                                              ref={mobileInputRef}
                                              type="text"
                                              value={query}
                                              maxLength={SEARCH_QUERY_MAX_LENGTH}
                                              variant="search"
                                              placeholder={placeholder}
                                              className={cn(
                                                  'w-full',
                                                  'h-10 rounded-[var(--corner-24)] border border-[var(--sand-90)] bg-[var(--sand-95)] py-2 text-base leading-6 tracking-[var(--tracking-body-l)] shadow-[0px_4px_12px_0px_rgba(0,0,0,0.05)] md:text-base',
                                                  'text-[color:var(--neutral-10)] caret-[color:var(--brown-30)]',
                                                  'focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--sand-90)]',
                                                  'pl-11 pr-12',
                                                  query.trim().length === 0
                                                      ? 'placeholder:text-[color:var(--neutral-10)]'
                                                      : 'placeholder:text-[color:var(--neutral-40)]'
                                              )}
                                              onChange={(e) => {
                                                  const value = e.target.value;
                                                  setSuppressMobileOverlay(false);
                                                  setQueryTooLong(false);
                                                  setQuery(value);
                                              }}
                                              onKeyDown={(e) => {
                                                  if (e.key === 'Escape') {
                                                      e.preventDefault();
                                                      closeMobileOverlay();
                                                      return;
                                                  }
                                                  if (e.key === 'ArrowDown' && mobileOverlayOpen) {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      focusFirstInSuggestionsSurface();
                                                      return;
                                                  }
                                                  if (e.key === 'Tab' && mobileOverlayOpen && !e.shiftKey) {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      focusFirstInSuggestionsSurface();
                                                  }
                                              }}
                                              aria-label={placeholder}
                                              aria-describedby={
                                                  queryTooLong
                                                      ? `${mobileSearchInputHintId} mobile-search-query-too-long`
                                                      : mobileSearchInputHintId
                                              }
                                              aria-invalid={queryTooLong || undefined}
                                              data-testid="header-search"
                                          />

                                          {query.trim().length === 0 ? (
                                              <SvgSearch
                                                  className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2"
                                                  aria-hidden
                                              />
                                          ) : (
                                              <>
                                                  <button
                                                      type="button"
                                                      onMouseDown={(e) => e.preventDefault()}
                                                      onClick={clearQuery}
                                                      className="absolute left-3 top-1/2 size-6 -translate-y-1/2 cursor-pointer text-[color:var(--neutral-10)] flex items-center justify-center"
                                                      aria-label={t('clearSearch', 'Clear search')}>
                                                      <SvgClose className="size-5" aria-hidden />
                                                  </button>
                                                  <button
                                                      type="submit"
                                                      onMouseDown={(e) => e.preventDefault()}
                                                      className="pointer-events-auto absolute right-3 top-1/2 size-6 -translate-y-1/2 cursor-pointer text-[color:var(--neutral-10)] flex items-center justify-center"
                                                      aria-label={t('submitSearch', 'Submit search')}>
                                                      <SvgRight className="size-5" aria-hidden />
                                                  </button>
                                              </>
                                          )}
                                      </div>
                                      {queryTooLong ? (
                                          <p
                                              id="mobile-search-query-too-long"
                                              className="mt-2 body-copy-s text-destructive"
                                              role="alert"
                                              data-testid="search-query-too-long">
                                              {tSearch('queryTooLong', { max: SEARCH_QUERY_MAX_LENGTH })}
                                          </p>
                                      ) : null}
                                  </form>
                              </div>
                              <button
                                  type="button"
                                  className="size-10 grid place-items-center cursor-pointer"
                                  aria-label={t('closeSearch', 'Close search')}
                                  onClick={() => closeMobileOverlay()}>
                                  <SvgClose className="size-5" aria-hidden />
                              </button>
                          </div>
                      </div>

                      <div ref={mobileSuggestionsPanelRef} className="pt-4">
                          <SearchSuggestionsPanel
                              variant="mobile"
                              query={query}
                              recentSearches={recentSearches}
                              trendingItems={trendingItems}
                              typingLeftItems={typingLeftItems}
                              typingCategoryItems={typingCategoryItems}
                              onNavigate={(link, options) => {
                                  closeAndNavigate(link, options);
                                  closeMobileOverlay(false);
                              }}
                              onRemoveRecent={removeRecentSearch}
                              onClearRecent={clearRecentSearches}
                              returnFocusRef={mobileInputRef}
                              //   onCloseSuggestions={() => closeMobileOverlay(true)}
                          />
                      </div>
                  </div>,
                  document.body
              )
            : null;

    if (variant === 'desktop') {
        return (
            <Popover
                modal={false}
                open={showSuggestions}
                onOpenChange={(open) => {
                    if (open) {
                        setShowSuggestions(true);
                        setIsFocused(true);
                    } else {
                        closeDesktopModal(true);
                    }
                }}>
                <PopoverTrigger asChild>
                    <button
                        ref={desktopSearchTriggerRef}
                        type="button"
                        data-testid="header-search-trigger"
                        aria-haspopup="dialog"
                        aria-expanded={showSuggestions}
                        className={desktopTriggerClassName}>
                        <span className="block truncate text-[color:var(--neutral-10)]">{placeholder}</span>
                        <SvgSearch
                            className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2"
                            aria-hidden
                        />
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    ref={popoverContentRef}
                    disableExitAnimation
                    className={popoverContentClassName}
                    style={
                        desktopSuggestionsWidth !== undefined
                            ? { width: desktopSuggestionsWidth, maxWidth: desktopSuggestionsWidth }
                            : undefined
                    }
                    align="start"
                    side="bottom"
                    sideOffset={-DESKTOP_SEARCH_TRIGGER_HEIGHT_PX}
                    aria-label={placeholder}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        closeDesktopModal(true);
                    }}>
                    <div
                        ref={desktopOverlayRootRef}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                e.preventDefault();
                                e.stopPropagation();
                                closeDesktopModal(true);
                            }
                        }}>
                        <form onSubmit={handleSubmit} className="relative z-10">
                            <span id={searchInputHintId} className="sr-only">
                                {openSuggestionsHint}
                            </span>
                            <div className="relative">
                                <Input
                                    id="desktop-search-input"
                                    ref={inputRef}
                                    type="text"
                                    value={query}
                                    maxLength={SEARCH_QUERY_MAX_LENGTH}
                                    variant="search"
                                    placeholder={placeholder}
                                    className={desktopInputClassName}
                                    onChange={handleInputChange}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Escape') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            closeDesktopModal(true);
                                            return;
                                        }
                                        if (e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            focusFirstInSuggestionsSurface();
                                        }
                                    }}
                                    aria-label={placeholder}
                                    aria-describedby={
                                        queryTooLong ? `${searchInputHintId} search-query-too-long` : searchInputHintId
                                    }
                                    aria-invalid={queryTooLong || undefined}
                                    data-testid="header-search"
                                />

                                <button
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={clearAndCloseDesktop}
                                    className="absolute left-3 top-1/2 size-6 -translate-y-1/2 cursor-pointer text-[color:var(--neutral-10)] flex items-center justify-center"
                                    aria-label={t('clearSearch', 'Clear search')}>
                                    <SvgClose className="size-5" aria-hidden />
                                </button>
                                {showControls && (
                                    <button
                                        type="submit"
                                        onMouseDown={(e) => e.preventDefault()}
                                        className="pointer-events-auto absolute right-3 top-1/2 size-6 -translate-y-1/2 cursor-pointer text-[color:var(--neutral-10)] flex items-center justify-center"
                                        aria-label={t('submitSearch', 'Submit search')}>
                                        <SvgRight className="size-5" aria-hidden />
                                    </button>
                                )}
                            </div>
                            {queryTooLong ? (
                                <p
                                    id="search-query-too-long"
                                    className="mt-2 body-copy-s text-destructive"
                                    role="alert"
                                    data-testid="search-query-too-long">
                                    {tSearch('queryTooLong', { max: SEARCH_QUERY_MAX_LENGTH })}
                                </p>
                            ) : null}
                        </form>
                        <div ref={desktopSuggestionsPanelRef}>
                            <SearchSuggestionsPanel
                                query={query}
                                recentSearches={recentSearches}
                                trendingItems={trendingItems}
                                typingLeftItems={typingLeftItems}
                                typingCategoryItems={typingCategoryItems}
                                onNavigate={closeAndNavigate}
                                onRemoveRecent={removeRecentSearch}
                                onClearRecent={clearRecentSearches}
                                variant="desktop"
                                attachedToInput
                                returnFocusRef={inputRef}
                            />
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        );
    }

    return (
        <>
            {mobileOverlay}
            <div className={cn('relative z-10', mobileOverlayOpen && 'hidden')}>
                <button
                    ref={mobileSearchTriggerRef}
                    type="button"
                    data-testid="header-search-trigger"
                    aria-haspopup="dialog"
                    aria-expanded={mobileOverlayOpen}
                    className={desktopTriggerClassName}
                    onClick={openMobileOverlay}>
                    <span className="block truncate text-[color:var(--neutral-10)]">{placeholder}</span>
                    <SvgSearch
                        className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2"
                        aria-hidden
                    />
                </button>
            </div>
        </>
    );
}
